import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import SkeletonTypography from './SkeletonTypography';
import ClientProvider from './client';
export default createDemo(import.meta.url, SkeletonTypography, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/material/components/skeleton/demos/typography/SkeletonTypography.tsx",
    "fileName": "SkeletonTypography.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import Typography from '@mui/material/Typography';\nimport type { TypographyProps } from '@mui/material/Typography';\nimport Skeleton from '@mui/material/Skeleton';\nimport Grid from '@mui/material/Grid';\n\nconst variants = [\n  'h1',\n  'h3',\n  'body1',\n  'caption',\n] as readonly TypographyProps['variant'][];\n\nfunction TypographyDemo(props: { loading?: boolean }) {\n  const { loading = false } = props;\n\n  return (\n    <div>\n      {variants.map((variant) => (\n        <Typography component=\"div\" key={variant} variant={variant}>\n          {loading ? <Skeleton /> : variant}\n        </Typography>\n      ))}\n    </div>\n  );\n}\n\nexport default function SkeletonTypography() {\n  return (\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 2,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    <Grid container spacing={8}>\n      <Grid size=\"grow\">\n        <TypographyDemo loading />\n      </Grid>\n      <Grid size=\"grow\">\n        <TypographyDemo />\n      </Grid>\n    </Grid>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  );\n}\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 38,
        "focusedLines": 8,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import Typography from '@mui/material/Typography';\nimport type { TypographyProps } from '@mui/material/Typography';\nimport Skeleton from '@mui/material/Skeleton';\nimport Grid from '@mui/material/Grid';\n\nconst variants = [\n  'h1',\n  'h3',\n  'body1',\n  'caption',\n] as readonly TypographyProps['variant'][];\n\nfunction TypographyDemo(props: { loading?: boolean }) {\n  const { loading = false } = props;\n\n  return (\n    <div>\n      {variants.map((variant) => (\n        <Typography component=\"div\" key={variant} variant={variant}>\n          {loading ? <Skeleton /> : variant}\n        </Typography>\n      ))}\n    </div>\n  );\n}\n\nexport default function SkeletonTypography() {\n  return (\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        "    <Grid container spacing={8}>\n      <Grid size=\"grow\">\n        <TypographyDemo loading />\n      </Grid>\n      <Grid size=\"grow\">\n        <TypographyDemo />\n      </Grid>\n    </Grid>\n"
      ],
      [
        "span",
        "frame",
        "  );\n}\n"
      ]
    ],
    "fallbackCritical": {
      "1": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 29
            },
            [
              "    <",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "container"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "spacing"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-c1 di-num",
                "8"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 30
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "size"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-s di-av",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "grow",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 31
            },
            [
              "        <",
              [
                "span",
                "pl-c1 di-jsx",
                "TypographyDemo"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "loading"
              ],
              " />"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 32
            },
            [
              "      </",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 33
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "size"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-s di-av",
                [
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ],
                  "grow",
                  [
                    "span",
                    "pl-pds",
                    "\""
                  ]
                ]
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 34
            },
            [
              "        <",
              [
                "span",
                "pl-c1 di-jsx",
                "TypographyDemo"
              ],
              " />"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 35
            },
            [
              "      </",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 36
            },
            [
              "    </",
              [
                "span",
                "pl-c1 di-jsx",
                "Grid"
              ],
              ">"
            ]
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 38,
    "focusedLines": 8,
    "collapsible": true,
    "externals": [
      "@mui/material/Typography",
      "@mui/material/Skeleton",
      "@mui/material/Grid"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
